import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from './api';
import { WINS_TICKER_AVAILABLE_IN_BUILD } from './growth-availability.ts';
import { HIDDEN_TICKER, parseTickerBody, type TickerView } from './growth-state.ts';

/**
 * CF-GROWTH-V1 — the caller-level owner of the public wins-ticker surface. A public endpoint, so
 * there is no auth state to manage; but the SAME event-race the bonus owner fixed applies (a
 * deposit/flip can fire while a read is in flight), so the surface carries a generation counter and
 * only the LATEST read publishes.
 *
 * CF-POT-COUNTER-REMOVE-V1 (PL TG #12916): this hook used to own a SECOND surface, the bonus-pool
 * counter, and fetched `GET /api/v1/bonus/pot` alongside the ticker. That surface was removed, and
 * the fetch went with it — the client no longer calls that endpoint at all. The route still exists
 * server-side (it shares helpers with the ticker), it simply has no caller.
 *
 * Two deliberate rules:
 *  - Build gate first: on a build that may not surface the ticker we NEVER call the server
 *    (WINS_TICKER_AVAILABLE_IN_BUILD). No request, no response to mis-render. Mirrors bonus-api.ts.
 *  - Publish ONLY on a successful, parseable response. A network/HTTP/parse failure leaves the
 *    current view untouched (the initial `hidden` covers "not known yet") — a transient blip never
 *    flickers a shown ticker to hidden, and a failure is never mistaken for "nothing to show". When
 *    the server DOES answer, its answer is authoritative: items:[] → hidden.
 *
 * Not a polling loop: refresh on mount and on the same balance-changing events the bonus owner uses.
 */

export type GrowthRefreshReason =
  | 'mount'
  | 'deposit_credited'
  | 'flip_settled'
  | 'withdrawal_completed'
  | 'failed_flip_refunded';

export interface GrowthStateOwner {
  readonly ticker: TickerView;
  /** Re-read the ticker. Never throws. */
  readonly refreshGrowth: (reason: GrowthRefreshReason) => void;
}

export function useGrowthState(apiBase: string): GrowthStateOwner {
  const [ticker, setTicker] = useState<TickerView>(HIDDEN_TICKER);

  const aliveRef = useRef(true);
  const baseRef = useRef(apiBase);
  const tickerGen = useRef(0);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);
  useEffect(() => { baseRef.current = apiBase; }, [apiBase]);

  const fetchTicker = useCallback(async () => {
    if (!WINS_TICKER_AVAILABLE_IN_BUILD) return;
    const gen = ++tickerGen.current;
    const base = baseRef.current;
    let view: TickerView | null = null;
    try {
      const resp = await apiFetch(`${base}/api/v1/ticker`, { method: 'GET' });
      if (resp.ok) view = parseTickerBody(await resp.json());
    } catch { /* network failure: leave the current view untouched */ }
    if (view !== null && aliveRef.current && gen === tickerGen.current && base === baseRef.current) {
      setTicker(view);
    }
  }, []);

  const refreshGrowth = useCallback((_reason: GrowthRefreshReason) => {
    void fetchTicker();
  }, [fetchTicker]);

  useEffect(() => { refreshGrowth('mount'); }, [refreshGrowth]);

  return { ticker, refreshGrowth };
}
